// src/components/modals/AddProductModal.jsx
import { useState, useEffect } from 'react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { X, Loader2, Package, ChevronDown } from 'lucide-react'
import ProductService from '../../services/productService'
import CategoryService from '../../services/categoryService'
import UnitsService from '../../services/unitsService'
import { ProductModel } from '../../utils/productModel'

const AddProductModal = ({ isOpen, onClose, onProductAdded, categories: categoriesProp = [], units: unitsProp = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    category: '',
    type: '',
    threshold: '',
    unit: '',
    cost: '',
    outletPrice: '',
    pickMePrice: '',
    uberPrice: '',
    isActive: true
  })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [categories, setCategories] = useState(categoriesProp)
  const [units, setUnits] = useState(unitsProp)
  // Removed dropdownsLoading state (no longer needed)

  // Get product types from service
  const productTypes = ProductService.getProductTypes()

  // Reset form when modal closes/opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        sku: '',
        category: '',
        type: '',
        threshold: '',
        unit: '',
        cost: '',
        outletPrice: '',
        pickMePrice: '',
        uberPrice: '',
        isActive: true
      })
      setErrors({})
      setCategories(categoriesProp)
      setUnits(unitsProp)
    }
  }, [isOpen, categoriesProp, unitsProp])

  // Auto-set unit to "pcs" when product type changes to "product"
  useEffect(() => {
    if (formData.type === 'product' && units.length > 0) {
      const pcsUnit = units.find(unit => 
        unit.abbreviation === 'pcs' || 
        unit.name.toLowerCase().includes('piece')
      )
      if (pcsUnit && formData.unit !== pcsUnit._id) {
        setFormData(prev => ({
          ...prev,
          unit: pcsUnit._id
        }))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.type, units, formData.unit])

  // Remove fetchDropdownData, use props instead

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }

    // Auto uppercase SKU
    if (name === 'sku') {
      setFormData(prev => ({
        ...prev,
        sku: value.toUpperCase()
      }))
    }
  }

  // Handle status toggle
  const handleStatusToggle = () => {
    setFormData(prev => ({
      ...prev,
      isActive: !prev.isActive
    }))
  }

  // Validate form
  const validateForm = () => {
    const validation = ProductModel.validateProduct(formData)
    
    // Custom validation for recipe_based products
    if (formData.type === 'recipe_based') {
      // Remove unit and threshold validation for recipe_based products
      delete validation.errors.unit
      delete validation.errors.threshold
    }
    
    setErrors(validation.errors)
    return Object.keys(validation.errors).length === 0
  }

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return

    setLoading(true)
    try {
      // Prepare data based on product type
      const productData = {
        name: formData.name.trim(),
        sku: formData.sku.trim().toUpperCase(),
        category: formData.category,
        type: formData.type,
        cost: parseFloat(formData.cost),
        outletPrice: parseFloat(formData.outletPrice),
        pickMePrice: parseFloat(formData.pickMePrice),
        uberPrice: parseFloat(formData.uberPrice),
        isActive: formData.isActive
      }

      // Only include unit and threshold for product type
      if (formData.type === 'product') {
        productData.unit = formData.unit
        productData.threshold = parseInt(formData.threshold)
      }
      
      const response = await ProductService.createProduct(productData)
      
      if (response.success) {
        const createdProduct = response.data?.product || productData
        onProductAdded(createdProduct)
        handleClose()
      } else {
        setErrors({ general: response.message || 'Failed to create product' })
      }
    } catch (error) {
      console.error('❌ Error creating product:', error)
      setErrors({ general: 'Failed to create product. Please try again.' })
    } finally {
      setLoading(false)
    }
  }

  // Handle modal close
  const handleClose = () => {
    setFormData({
      name: '',
      sku: '',
      category: '',
      type: '',
      threshold: '',
      unit: '',
      cost: '',
      outletPrice: '',
      pickMePrice: '',
      uberPrice: '',
      isActive: true
    })
    setErrors({})
    onClose()
  }

  // Check if current product type is recipe_based
  const isRecipeBased = formData.type === 'recipe_based'

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleClose} />
        
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-6 pt-6 pb-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              {/* <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <Package className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Add New Product</h3>
                </div>
              </div> */}
              <h3 className="text-lg font-medium text-gray-900">Add New Product</h3>
              <button
              onClick={handleClose}
              disabled={loading}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <X className="h-6 w-6" />
            </button>
            </div>

            {/* Form */}
            <div className="space-y-4">
              {/* General Error */}
              {errors.general && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-600 text-sm">{errors.general}</p>
                </div>
              )}

              {/* Row 1: Name & SKU */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Name *
                  </label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g. Espresso Standard"
                    disabled={loading}
                    className={errors.name ? 'border-red-300' : ''}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SKU *
                  </label>
                  <Input
                    name="sku"
                    value={formData.sku}
                    onChange={handleInputChange}
                    placeholder="e.g. ES1200"
                    disabled={loading}
                    className={errors.sku ? 'border-red-300' : ''}
                  />
                  {errors.sku && (
                    <p className="text-red-500 text-sm mt-1">{errors.sku}</p>
                  )}
                </div>
              </div>

              {/* Row 2: Category & Product Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <div className="relative">
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      disabled={loading}
                      className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 disabled:opacity-50 disabled:cursor-not-allowed appearance-none ${
                        errors.category ? 'border-red-300' : 'border-gray-200'
                      }`}
                    >
                      <option value="">Select category</option>
                      {categories.map(category => (
                        <option key={category._id} value={category._id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                  {errors.category && (
                    <p className="text-red-500 text-sm mt-1">{errors.category}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Type *
                  </label>
                  <div className="relative">
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      disabled={loading}
                      className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 disabled:opacity-50 disabled:cursor-not-allowed appearance-none ${
                        errors.type ? 'border-red-300' : 'border-gray-200'
                      }`}
                    >
                      <option value="">Select Product Type</option>
                      {productTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.icon} {type.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                  {errors.type && (
                    <p className="text-red-500 text-sm mt-1">{errors.type}</p>
                  )}
                </div>
              </div>

              {/* Row 3: Threshold & Unit - Conditional Based on Product Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Threshold {!isRecipeBased && '*'}
                  </label>
                  <Input
                    type="number"
                    name="threshold"
                    value={formData.threshold}
                    onChange={handleInputChange}
                    placeholder="Enter threshold (e.g., 10)"
                    min="0"
                    disabled={loading || isRecipeBased}
                    readOnly={isRecipeBased}
                    className={`${errors.threshold ? 'border-red-300' : ''} ${isRecipeBased ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  />
                  {isRecipeBased && (
                    <p className="text-xs text-gray-500 mt-1">Not applicable for recipe-based products</p>
                  )}
                  {errors.threshold && (
                    <p className="text-red-500 text-sm mt-1">{errors.threshold}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit {!isRecipeBased && '*'}
                  </label>
                  <div className="relative">
                    <select
                      name="unit"
                      value={formData.unit}
                      onChange={handleInputChange}
                      disabled={loading || isRecipeBased || formData.type === 'product'}
                      className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 disabled:opacity-50 disabled:cursor-not-allowed appearance-none ${
                        errors.unit ? 'border-red-300' : 'border-gray-200'
                      } ${(isRecipeBased || formData.type === 'product') ? 'bg-gray-100' : ''}`}
                    >
                      <option value="">Select unit</option>
                      {units.map(unit => (
                        <option key={unit._id} value={unit._id}>
                          {unit.name} ({unit.abbreviation})
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                  {isRecipeBased && (
                    <p className="text-xs text-gray-500 mt-1">Not applicable for recipe-based products</p>
                  )}
                  {formData.type === 'product' && !isRecipeBased && (
                    <p className="text-xs text-gray-500 mt-1">Unit automatically set to 'pcs' for products</p>
                  )}
                  {errors.unit && (
                    <p className="text-red-500 text-sm mt-1">{errors.unit}</p>
                  )}
                </div>
              </div>

              {/* Row 4: Cost & Outlet Price */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cost (Rs.) *
                  </label>
                  <Input
                    type="number"
                    name="cost"
                    value={formData.cost}
                    onChange={handleInputChange}
                    placeholder="Enter cost (e.g., 120.50)"
                    min="0"
                    step="0.01"
                    disabled={loading}
                    className={errors.cost ? 'border-red-300' : ''}
                  />
                  {errors.cost && (
                    <p className="text-red-500 text-sm mt-1">{errors.cost}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Outlet Price (Rs.) *
                  </label>
                  <Input
                    type="number"
                    name="outletPrice"
                    value={formData.outletPrice}
                    onChange={handleInputChange}
                    placeholder="Enter outlet price (e.g., 200.00)"
                    min="0"
                    step="0.01"
                    disabled={loading}
                    className={errors.outletPrice ? 'border-red-300' : ''}
                  />
                  {errors.outletPrice && (
                    <p className="text-red-500 text-sm mt-1">{errors.outletPrice}</p>
                  )}
                </div>
              </div>

              {/* Row 5: PickMe Price & Uber Price */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    PickMe Price (Rs.) *
                  </label>
                  <Input
                    type="number"
                    name="pickMePrice"
                    value={formData.pickMePrice}
                    onChange={handleInputChange}
                    placeholder="Enter PickMe price (e.g., 220.00)"
                    min="0"
                    step="0.01"
                    disabled={loading}
                    className={errors.pickMePrice ? 'border-red-300' : ''}
                  />
                  {errors.pickMePrice && (
                    <p className="text-red-500 text-sm mt-1">{errors.pickMePrice}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Uber Price (Rs.) *
                  </label>
                  <Input
                    type="number"
                    name="uberPrice"
                    value={formData.uberPrice}
                    onChange={handleInputChange}
                    placeholder="Enter Uber price (e.g., 225.00)"
                    min="0"
                    step="0.01"
                    disabled={loading}
                    className={errors.uberPrice ? 'border-red-300' : ''}
                  />
                  {errors.uberPrice && (
                    <p className="text-red-500 text-sm mt-1">{errors.uberPrice}</p>
                  )}
                </div>
              </div>

              {/* Status Toggle */}
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <button
                  type="button"
                  onClick={handleStatusToggle}
                  disabled={loading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formData.isActive ? 'bg-orange-500' : 'bg-gray-200'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      formData.isActive ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-6">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={loading}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 bg-black hover:bg-gray-800 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Creating...
                  </>
                ) : (
                  'Create Product'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AddProductModal